/**
 * Review-prompt gating.
 *
 * Decides WHEN to show the 5-star modal so we don't nag users on
 * their first meal (they haven't seen enough value yet) or repeatedly
 * (poison the trust). Standard mobile-app pattern:
 *
 *   • Show ONLY after `MIN_MEALS_BEFORE_PROMPT` meals logged in total.
 *   • Show ONLY after the user has spent ≥ `MIN_DAYS_SINCE_INSTALL` days
 *     in the app (measured from the first successful meal we log).
 *   • Never re-prompt sooner than `COOLDOWN_DAYS` after a dismissal or
 *     submission.
 *   • Never prompt more than `MAX_LIFETIME_PROMPTS` times per install.
 *
 * State is stored in localStorage (works identically on web, iOS
 * Capacitor, and Android Capacitor WebView).
 */

const K_INSTALL_AT     = "calofay_review_install_at";
const K_MEAL_COUNT     = "calofay_review_meal_count";
const K_LAST_SHOWN_AT  = "calofay_review_last_shown_at";
const K_TOTAL_SHOWN    = "calofay_review_total_shown";
const K_SUBMITTED      = "calofay_review_submitted";        // "5" | "low" — never re-ask after a 5-star store handoff

const MIN_MEALS_BEFORE_PROMPT = 3;
const MIN_DAYS_SINCE_INSTALL  = 1;   // give ≥ 24 h between install and first ask
const COOLDOWN_DAYS           = 14;
const MAX_LIFETIME_PROMPTS    = 3;
const DAY_MS = 24 * 3600 * 1000;

function _get(k) { try { return localStorage.getItem(k); } catch { return null; } }
function _set(k, v) { try { localStorage.setItem(k, String(v)); } catch { /* ignore */ } }

/** Called on every successful "meal saved" event — increments the
 * counter used to decide when the user has been active enough. */
export function noteMealSaved() {
  if (!_get(K_INSTALL_AT)) _set(K_INSTALL_AT, Date.now());
  const cur = parseInt(_get(K_MEAL_COUNT) || "0", 10);
  _set(K_MEAL_COUNT, cur + 1);
}

/** Returns true if we should show the review prompt right now. */
export function shouldPromptForReview() {
  // Already handed off to the store → never ask again on this install.
  if (_get(K_SUBMITTED) === "5") return false;
  // Cap lifetime prompts.
  const totalShown = parseInt(_get(K_TOTAL_SHOWN) || "0", 10);
  if (totalShown >= MAX_LIFETIME_PROMPTS) return false;
  // Cooldown since the last dismissal.
  const lastShown = parseInt(_get(K_LAST_SHOWN_AT) || "0", 10);
  if (lastShown && Date.now() - lastShown < COOLDOWN_DAYS * DAY_MS) return false;
  // Enough activity?
  const meals = parseInt(_get(K_MEAL_COUNT) || "0", 10);
  if (meals < MIN_MEALS_BEFORE_PROMPT) return false;
  const installedAt = parseInt(_get(K_INSTALL_AT) || "0", 10);
  if (installedAt && Date.now() - installedAt < MIN_DAYS_SINCE_INSTALL * DAY_MS) return false;
  return true;
}

/** Called when we actually SHOW the modal — records timing so the
 * cooldown clock starts even if the user just closes the sheet. */
export function markPrompted() {
  _set(K_LAST_SHOWN_AT, Date.now());
  const t = parseInt(_get(K_TOTAL_SHOWN) || "0", 10);
  _set(K_TOTAL_SHOWN, t + 1);
}

/** Called after the user submits a rating. 5 stars → store handoff,
 * so we permanently silence future prompts. */
export function markSubmitted(rating) {
  _set(K_SUBMITTED, rating >= 5 ? "5" : "low");
}
